import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/** Monday = 0 … Sunday = 6 (matches backend Course.day_of_week / Python weekday). */
export const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
export const WEEKDAYS_LONG = [
  'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag',
];

const pad2 = (n) => String(n).padStart(2, '0');

export function parseDate(value) {
  if (!value && value !== 0) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s || s === '-') return null;

  const de = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[,\s].*)?$/);
  if (de) {
    const d = new Date(Number(de[3]), Number(de[2]) - 1, Number(de[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const isoDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDate) {
    const d = new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Display dates as 01.01.2026 (always two-digit day/month). */
export function formatDate(date) {
  const d = parseDate(date);
  if (!d) return '';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Display times as 15:30 (no seconds). */
export function formatTime(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return `${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
  }
  const s = String(value).trim();
  if (!s || s === '-') return '';
  const m = s.match(/(\d{1,2})[:.](\d{2})/);
  if (!m) return s;
  return `${pad2(m[1])}:${m[2]}`;
}

export function addMinutesToTime(t, mins) {
  if (!t && t !== 0) return '';
  const m = String(t).match(/(\d{1,2})[:.](\d{2})/);
  if (!m) return '';
  const total = Number(m[1]) * 60 + Number(m[2]) + (Number(mins) || 0);
  const wrapped = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

/** Display a time span as 15:30 - 16:15 */
export function formatTimeRange(start, end, sep = ' - ') {
  const a = formatTime(start);
  const b = formatTime(end);
  if (a && b) return `${a}${sep}${b}`;
  return a || b || '';
}

export function formatTimeRangeUhr(start, end) {
  const r = formatTimeRange(start, end);
  return r ? `${r} Uhr` : '';
}

/** e.g. "Do 15:30 - 16:15 Uhr" */
export function formatWeekdayTimeRange(dayOfWeek, start, end) {
  const wd = formatWeekdayShort(dayOfWeek);
  const range = formatTimeRangeUhr(start, end);
  if (wd && range) return `${wd} ${range}`;
  return range || wd || '';
}

export function formatDateTime(date) {
  const d = parseDate(date);
  if (!d) return '';
  return `${formatDate(d)} ${formatTime(d)}`;
}

export function formatWeekdayShort(dayOfWeek) {
  if (dayOfWeek == null || dayOfWeek === '') return '';
  return WEEKDAYS_SHORT[Number(dayOfWeek)] || '';
}

export function formatWeekdayAbbr(token) {
  const t = String(token || '').replace(/\./g, '').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

export function formatDateWithWeekday(date, { weekday = 'short', time = false, year = true } = {}) {
  const d = parseDate(date);
  if (!d) return '';
  const idx = (d.getDay() + 6) % 7;
  const wd = weekday === 'long' ? WEEKDAYS_LONG[idx] : WEEKDAYS_SHORT[idx];
  const datePart = year ? formatDate(d) : formatDate(d).slice(0, 5);
  if (time) return `${wd}, ${datePart}, ${formatTime(d)} Uhr`;
  return `${wd}, ${datePart}`;
}

/** Chat/list timestamps: today → 18:30, this week → Mi 18:30, else 01.01.2026. */
export function formatRelativeDateTime(date) {
  const d = parseDate(date);
  if (!d) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(d);
  const diffD = (now - d) / 86400000;
  if (diffD >= 0 && diffD < 7) {
    return `${WEEKDAYS_SHORT[(d.getDay() + 6) % 7]} ${formatTime(d)}`;
  }
  return formatDate(d);
}

/**
 * Drop the teacher parenthetical from iMikel-style course names.
 * "Kinder-Tanz (Elfering) Mo 14.30" → "Kinder-Tanz Mo 14.30"
 * Keeps groups that contain digits, e.g. "(10er)".
 */
export function stripTeacherFromScheduleLabel(text) {
  if (text == null || text === '') return '';
  return String(text)
    .replace(
      /\s*\(([^)]+)\)(?=\s+(?:Mo|Di|Mi|Do|Fr|Sa|So)\b|\s+\d{1,2}[:.]\d{2}|$)/gi,
      (full, inner) => {
        const t = String(inner).trim();
        if (!t || t === '—' || t === '-') return '';
        if (/\d/.test(t)) return full;
        if (/^[A-Za-zÄÖÜäöüßÀ-ÿ][A-Za-zÄÖÜäöüßÀ-ÿ'\-., ]{0,60}$/.test(t)) return '';
        return full;
      },
    )
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const WEEKDAY_LONG_BY_KEY = {
  montag: 'Montag', dienstag: 'Dienstag', mittwoch: 'Mittwoch', donnerstag: 'Donnerstag',
  freitag: 'Freitag', samstag: 'Samstag', sonntag: 'Sonntag',
};
const WEEKDAY_SHORT_BY_KEY = {
  mo: 'Mo', di: 'Di', mi: 'Mi', do: 'Do', fr: 'Fr', sa: 'Sa', so: 'So',
};

/** mo/montag → Mo/Montag. Längere Wörter zuerst, damit „montag“ nicht zu „Mo“ wird. */
function capitalizeWeekdays(text) {
  return String(text).replace(
    /\b(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|mo|di|mi|do|fr|sa|so)\.?\b/gi,
    (token) => {
      const key = token.toLowerCase().replace(/\./g, '');
      return WEEKDAY_LONG_BY_KEY[key] || WEEKDAY_SHORT_BY_KEY[key] || token;
    },
  );
}

/** Uhrzeiten in einem Text als HH:MM, Wochentage groß. Namen bleiben. */
export function formatClocks(text) {
  if (text == null || text === '') return '';
  let s = String(text);
  const dates = [];
  s = s.replace(/\b(\d{1,2})\.(\d{1,2})\.(\d{4})\b/g, (m) => {
    dates.push(formatDate(m) || m);
    return `\u0000D${dates.length - 1}\u0000`;
  });
  s = s.replace(/\b(\d{1,2})[:.](\d{2})(?::\d{2}(?:\.\d+)?)?\b/g, (_, h, min) => formatTime(`${h}:${min}`));
  s = capitalizeWeekdays(s);
  return s.replace(/\u0000D(\d+)\u0000/g, (_, i) => dates[Number(i)]);
}

/**
 * Normalise free-text that may contain weekday abbreviations and clock times,
 * e.g. "Pop Chor (Fischer) mi 18:30:00" → "Pop Chor Mi 18:30".
 */
/** Zwei Schüler zur gleichen Zeit sind eine 2er Gruppe, nicht Einzelunterricht. */
export function lessonFormForHeadcount(label, count) {
  const text = String(label || '');
  const n = Number(count) || 0;
  if (n < 2 || !/\bEinzel\b/i.test(text)) return text;
  const form = n === 2 ? '2er Gruppe' : 'Gruppe';
  return text.replace(/\bEinzel\b/gi, form);
}

export function formatScheduleLabel(text) {
  if (text == null || text === '') return '';
  let s = formatClocks(text);
  s = s.replace(/\b(mo|di|mi|do|fr|sa|so)\.?\b/gi, (w) => formatWeekdayAbbr(w));
  s = s.replace(
    /\b((?:Mo|Di|Mi|Do|Fr|Sa|So)\s+)?(\d{2}:\d{2})(?:\s*[-–]\s*|\s+)(\d{2}:\d{2})(?:\s*Uhr)?\b/g,
    (_, wd, a, b) => `${wd || ''}${a} - ${b} Uhr`,
  );
  return stripTeacherFromScheduleLabel(s);
}

/** Importierte Fächer heißen oft kombiniert („Klavier Einzel 45 Min“). */
export const SUBJECT_SPLIT_RE = /^(.*?)\s+((?:Einzel|Gruppe|\d+er|E\s*\d+er|\d+\s*Min|Bonus|Mini)\b.*)$/i;

/** Fach ohne Tarif-/Form-Suffix, z. B. „Keyboard Einzel 30 Min“ → „Keyboard“. */
export function subjectBaseName(name) {
  const n = String(name || '').trim();
  if (!n) return n;
  return n.match(SUBJECT_SPLIT_RE)?.[1]?.trim() || n;
}

export function subjectFormSuffix(name) {
  const n = String(name || '').trim();
  if (!n) return null;
  return n.match(SUBJECT_SPLIT_RE)?.[2]?.trim() || null;
}

const PREFIX_IS_FORM = /(Einzel|Gruppe|\d+\s*er|Karte|\d+\s*Min|Bonus|Mini)/i;

/** Tarif ohne vorangestelltes Instrument: „Klavier Einzel 30 Min“ → „Einzel 30 Min“. */
export function tariffFormName(name) {
  const n = String(name || '').trim();
  const m = n.match(SUBJECT_SPLIT_RE);
  if (!m) return n;
  const prefix = m[1].trim();
  const form = m[2].trim();
  if (!prefix || prefix.includes(' ') || prefix.includes('/')) return n;
  if (PREFIX_IS_FORM.test(prefix)) return n;
  return form;
}

/**
 * Sichtbare Kursbezeichnung: individuelle Bezeichnung, sonst Fach.
 * Tarif/Gebühr bleibt unberührt.
 */
export function courseDisplayName(name, subjectName) {
  const bez = String(name || '').trim();
  if (bez && bez !== '-') return formatScheduleLabel(bez) || bez;
  return subjectBaseName(subjectName) || String(subjectName || '').trim();
}

/** Vocalunterricht → Vokalunterricht (k, nicht c). */
export function vokalSpelling(text) {
  if (text == null || text === '') return text || '';
  return String(text)
    .replace(/Vocalunterricht/g, 'Vokalunterricht')
    .replace(/Vocalfächer/g, 'Vokalfächer');
}

/** Kurszeile für Anwesenheitslisten — ohne Sekunden, ohne doppelte Wochentag/Uhrzeit. */
export function courseHeaderLabel(courseName, { dayOfWeek, startTime, isOnline = false } = {}) {
  const name = formatScheduleLabel(courseName) || courseName || '';
  const hasTime = /\b\d{2}:\d{2}\b/.test(name);
  const hasWd = /\b(Mo|Di|Mi|Do|Fr|Sa|So)\b/.test(name);
  const bits = [name];
  if (dayOfWeek != null && !hasWd) bits.push(WEEKDAYS_SHORT[Number(dayOfWeek)] || '');
  if (startTime && !hasTime) bits.push(formatTime(startTime));
  if (isOnline) bits.push('ONLINE');
  return bits.filter(Boolean).join(' · ');
}

/** Best-effort display formatting for read-only fields. */
export function formatDisplayValue(value, type = 'text') {
  if (value == null || value === '') return '';
  if (value === '-') return '-';
  if (type === 'date') return formatDate(value) || String(value);
  if (type === 'time') return formatTime(value) || String(value);
  if (type === 'datetime-local') return formatDateTime(value) || String(value);
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return formatDate(s) || s;
  if (/^\d{4}-\d{2}-\d{2}[T\s]/.test(s)) return formatDateTime(s) || s;
  if (/^\d{1,2}:\d{2}(:\d{2}(?:\.\d+)?)?$/.test(s)) return formatTime(s);
  // Free text with weekday abbr (mo/di/…) and/or clock times → Xx + 18:30
  if (/\b(?:mo|di|mi|do|fr|sa|so)\.?\b/i.test(s) || /\b\d{1,2}[:.]\d{2}(?::\d{2})?\b/.test(s)) {
    return formatScheduleLabel(s);
  }
  return s;
}

const REMARK_MARKERS = ['handy:', 'e-mail:', 'email:', 'tel.:', 'tel:', 'möchte', 'moechte'];

/** Freitext einer Belegung, kein Personen- oder Kontoname. */
export function isImportedRemark(text) {
  const s = String(text || '').trim();
  if (!s) return false;
  const low = s.toLowerCase();
  if (REMARK_MARKERS.some((marker) => low.includes(marker))) return true;
  return s.length > 80 && /[.!?]/.test(s);
}

const MAIL_MARK = /(?:e-mail|email|mail)\s*:/gi;
const EMAIL_ADDR = /^[^\s@]+@[^\s@]+\.[^\s@]+\s*/;
const TRAILING_DATE = /\s+\d{1,2}\.\d{1,2}\.\d{4}\s*$/;
const TITLES = new Set(['herrn', 'herr', 'frau', 'familie', 'dr']);

function nameWord(token) {
  const word = String(token || '').replace(/[.,;:]+$/g, '');
  const kern = word.replace(/[-']/g, '');
  if (!kern || !/^[\p{L}]+$/u.test(kern)) return null;
  if (kern.length >= 2 && kern.length <= 3 && kern === kern.toUpperCase()) return null;
  return word;
}

function namePair(tokens, limit = 6) {
  const words = [];
  for (const token of tokens) {
    const word = nameWord(token);
    if (!word) break;
    if (TITLES.has(word.toLowerCase())) continue;
    words.push(word);
    if (words.length >= limit) break;
  }
  if (words.length < 2) return null;
  return { last: words[0], first: words.slice(1).join(' ') };
}

/** Nachname und Vorname aus einer importierten Bemerkung. */
export function personNameFromRemark(text) {
  const s = String(text || '').trim();
  if (!isImportedRemark(s)) return null;
  const marks = [...s.matchAll(MAIL_MARK)];
  if (marks.length) {
    const last = marks[marks.length - 1];
    const rest = s.slice(last.index + last[0].length).trim().replace(EMAIL_ADDR, '');
    const found = namePair(rest.split(/\s+/));
    if (found) return found;
  }
  const rest = s.replace(TRAILING_DATE, '').trim();
  const tail = [];
  for (const token of rest.split(/\s+/).reverse()) {
    const word = nameWord(token);
    if (!word) break;
    if (TITLES.has(word.toLowerCase())) continue;
    tail.push(word);
    if (tail.length >= 4) break;
  }
  tail.reverse();
  if (tail.length < 2) return null;
  return { last: tail[0], first: tail.slice(1).join(' ') };
}

function recoveredName(firstName, lastName, holder) {
  const blobs = [
    `${firstName || ''} ${lastName || ''}`,
    `${lastName || ''} ${firstName || ''}`,
    holder || '',
    firstName || '',
    lastName || '',
  ];
  for (const blob of blobs) {
    const found = personNameFromRemark(blob);
    if (found) return found;
  }
  return null;
}

/** Anzeige „Nachname, Vorname“, Bemerkung durch den Namen am Ende ersetzen. */
export function payerNameLabel(lastName, firstName) {
  const last = String(lastName || '').trim();
  const first = String(firstName || '').trim();
  const combined = [last, first].filter(Boolean).join(', ');
  const blob = `${last} ${first}`.trim();
  const remark = isImportedRemark(combined) || isImportedRemark(blob) || isImportedRemark(last) || isImportedRemark(first);
  if (!remark) return combined || '—';
  const found = recoveredName(first, last, '');
  if (!found) return '—';
  return `${found.last}, ${found.first}`;
}

/** Kontoinhaber: Bemerkung ausblenden, sonst den Namen der Person. */
export function accountHolderLabel(holder, firstName, lastName) {
  const stored = String(holder || '').trim();
  if (stored && !isImportedRemark(stored)) return stored;
  const found = recoveredName(firstName, lastName, stored);
  if (found) return `${found.last}, ${found.first}`;
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (name && !isImportedRemark(name)) return name;
  return '—';
}

/** Überschrift der Beitragsaufstellung, ohne importierte Bemerkung. */
export function payerHeading(firstName, lastName) {
  const found = recoveredName(firstName, lastName, '');
  if (found) return `${found.first} ${found.last}`.trim();
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (!name || isImportedRemark(name) || isImportedRemark(firstName) || isImportedRemark(lastName)) return '';
  return name;
}

/** Deutsche Zahl: Tausenderpunkt, Dezimalkomma. 23983.68 → 23.983,68 */
export function formatDeNumber(value, fractionDigits = 2) {
  const n = Number(value);
  const digits = Number.isFinite(fractionDigits) ? fractionDigits : 2;
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  }).format(safe);
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === '') return '0,00 €';
  return `${formatDeNumber(amount)} €`;
}
